// Entry sequence logic - Boot sequence, typed text, fragment overlays
// For "The House of Chopras" - Terminal aesthetic intro
import {
  typeText,
  createCursor,
  prefersReducedMotion,
  instantText,
  textScramble,
  corruptedReveal,
  gsap
} from './animations';
import { navigateTo } from './router';
import { glitchText } from './effects';
import excerpts from '../content/excerpts.json';

const STORAGE_KEY = 'chopras_intro_seen';

// Mobile detection threshold (768px matches CSS media queries)
const MOBILE_BREAKPOINT = 768;

/**
 * Check if the current device is mobile based on viewport width
 */
function isMobileViewport(): boolean {
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

// Timings for the sequence (in milliseconds)
// Desktop timings
const DESKTOP_CURSOR_WAIT = 2000;           // 2s: Pure black with cursor
const DESKTOP_FRAGMENT_PHASE_DURATION = 14000; // 14s: Overlapping fragments
const DESKTOP_FRAGMENT_COUNT = 8;           // Number of fragments to show
const DESKTOP_FRAGMENT_LINGER_BASE = 2000;  // Base linger time for fragments
const DESKTOP_FRAGMENT_LINGER_VARIANCE = 2000; // Random variance for linger

// Mobile timings - shorter for better mobile UX
const MOBILE_CURSOR_WAIT = 1200;            // 1.2s: Shorter initial pause
const MOBILE_FRAGMENT_PHASE_DURATION = 8000; // 8s: Faster fragment phase
const MOBILE_FRAGMENT_COUNT = 5;            // Fewer fragments to reduce clutter
const MOBILE_FRAGMENT_LINGER_BASE = 1200;   // Shorter linger time
const MOBILE_FRAGMENT_LINGER_VARIANCE = 1200; // Less variance

/**
 * Get timing configuration based on device type
 */
function getTimingConfig() {
  const isMobile = isMobileViewport();
  return {
    cursorWait: isMobile ? MOBILE_CURSOR_WAIT : DESKTOP_CURSOR_WAIT,
    fragmentPhaseDuration: isMobile ? MOBILE_FRAGMENT_PHASE_DURATION : DESKTOP_FRAGMENT_PHASE_DURATION,
    fragmentCount: isMobile ? MOBILE_FRAGMENT_COUNT : DESKTOP_FRAGMENT_COUNT,
    fragmentLingerBase: isMobile ? MOBILE_FRAGMENT_LINGER_BASE : DESKTOP_FRAGMENT_LINGER_BASE,
    fragmentLingerVariance: isMobile ? MOBILE_FRAGMENT_LINGER_VARIANCE : DESKTOP_FRAGMENT_LINGER_VARIANCE,
    // Scale other delays for mobile
    bootLineDelay: isMobile ? 200 : 300,
    postBootPause: isMobile ? 300 : 500,
    glitchFadeDelay: isMobile ? 200 : 300,
    postGlitchDelay: isMobile ? 250 : 400,
    postFragmentDelay: isMobile ? 600 : 1000,
    fadeOutDelay: isMobile ? 400 : 600,
    transitionPause: isMobile ? 500 : 800,
    transitionFadeDelay: isMobile ? 250 : 400,
    questionRevealDelay: isMobile ? 300 : 500,
  };
}

// Legacy constants removed - use getTimingConfig() instead

// Track running animations for cleanup
let activeTimelines: gsap.core.Timeline[] = [];
let activeTimeouts: number[] = [];
let isSequenceAborted = false;

// Types for excerpts
interface Excerpt {
  id: string;
  text: string;
  phase: number;
  duration: number;
  style: string;
  position?: string;
}

/**
 * Check if user has seen the intro before
 */
export function hasSeenIntro(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Mark intro as seen
 */
export function markIntroSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // localStorage not available
  }
}

/**
 * Skip the intro and go to house
 */
export function skipIntro(): void {
  isSequenceAborted = true;
  cleanupEntrySequence();
  markIntroSeen();
  navigateTo('house');
}

/**
 * Add delay helper that can be cancelled
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => {
    const id = window.setTimeout(resolve, ms);
    activeTimeouts.push(id);
  });
}

/**
 * Run the entry sequence
 */
export async function runEntrySequence(container: HTMLElement): Promise<void> {
  isSequenceAborted = false;
  const reducedMotion = prefersReducedMotion();
  const seenBefore = hasSeenIntro();

  // Create skip button (visible throughout)
  const skipButton = document.createElement('button');
  skipButton.className = 'entry-skip';
  skipButton.textContent = '> SKIP';
  skipButton.setAttribute('aria-label', 'Skip introduction and enter the site');
  skipButton.setAttribute('type', 'button');
  skipButton.addEventListener('click', skipIntro);
  container.appendChild(skipButton);

  // Focus the skip button for keyboard accessibility
  // Use requestAnimationFrame to ensure the DOM is ready
  requestAnimationFrame(() => {
    skipButton.focus();
  });

  // Track if user can proceed by clicking
  let canProceed = false;

  const proceedHandler = (e: MouseEvent | KeyboardEvent) => {
    // Ignore skip button clicks
    if (e.target === skipButton) return;
    if (canProceed && !isSequenceAborted) {
      skipIntro();
    }
  };

  container.addEventListener('click', proceedHandler);
  document.addEventListener('keydown', proceedHandler);

  // Store handlers for cleanup
  (container as any).__proceedHandler = proceedHandler;

  if (reducedMotion) {
    await runReducedMotionSequence(container);
    canProceed = true;
  } else if (seenBefore) {
    await runAbbreviatedSequence(container);
    canProceed = true;
  } else {
    await runFullSequence(container);
    canProceed = true;
    markIntroSeen();
  }
}

/**
 * Reduced motion version - instant display
 */
async function runReducedMotionSequence(container: HTMLElement): Promise<void> {
  const textElement = document.createElement('div');
  textElement.className = 'entry-text entry-text--center';
  container.appendChild(textElement);

  instantText(textElement, '> WHO IS CARLOS CHOPRA?');

  const prompt = document.createElement('div');
  prompt.className = 'entry-prompt';
  prompt.textContent = '[Click anywhere to enter]';
  container.appendChild(prompt);
}

/**
 * Abbreviated version for returning visitors
 */
async function runAbbreviatedSequence(container: HTMLElement): Promise<void> {
  if (isSequenceAborted) return;

  // Create blinking cursor first
  const cursor = createCursor(container);
  cursor.className = 'entry-cursor';

  await delay(500);
  if (isSequenceAborted) return;

  // Terminal text
  const textElement = document.createElement('div');
  textElement.className = 'entry-text entry-text--center';
  container.appendChild(textElement);

  // Remove standalone cursor, add inline cursor
  cursor.remove();

  await new Promise<void>(resolve => {
    const tween = typeText(textElement, '> RECONNECTING...', 0.8);
    tween.eventCallback('onComplete', resolve);
  });

  await delay(800);
  if (isSequenceAborted) return;

  // Clear and show question
  textElement.textContent = '';
  await new Promise<void>(resolve => {
    const tween = typeText(textElement, '> WHO IS CARLOS CHOPRA?', 1.5);
    tween.eventCallback('onComplete', resolve);
  });

  // Add prompt
  await delay(500);
  if (isSequenceAborted) return;

  const prompt = document.createElement('div');
  prompt.className = 'entry-prompt';
  prompt.textContent = '[Click anywhere to enter]';
  prompt.style.opacity = '0';
  container.appendChild(prompt);

  gsap.to(prompt, { opacity: 1, duration: 0.5 });
}

/**
 * Full intro sequence with all phases
 */
async function runFullSequence(container: HTMLElement): Promise<void> {
  if (isSequenceAborted) return;

  // Get timing configuration based on device type
  const timing = getTimingConfig();

  // Create fragments container for overlapping text
  const fragmentsContainer = document.createElement('div');
  fragmentsContainer.className = 'entry-fragments';
  container.appendChild(fragmentsContainer);

  // Phase 1: Pure black with blinking cursor (shorter on mobile)
  const cursor = createCursor(container);
  cursor.className = 'entry-cursor entry-cursor--initial';

  await delay(timing.cursorWait);
  if (isSequenceAborted) return;

  // Move cursor to text area
  cursor.classList.remove('entry-cursor--initial');
  cursor.classList.add('entry-cursor--inline');

  // Phase 1b: Boot sequence (faster on mobile)
  await runBootPhase(container, cursor, timing);
  if (isSequenceAborted) return;

  // Phase 2: Fragment overlay phase (shorter on mobile)
  await runFragmentPhase(fragmentsContainer, timing);
  if (isSequenceAborted) return;

  // Phase 3: Coalesce to question (faster on mobile)
  await runFinalPhase(container, fragmentsContainer, timing);
}

/**
 * Phase 1: Boot/connection messages
 */
async function runBootPhase(
  container: HTMLElement,
  cursor: HTMLElement,
  timing: ReturnType<typeof getTimingConfig>
): Promise<void> {
  const bootMessages = (excerpts as Excerpt[]).filter(e => e.phase === 1);

  const bootContainer = document.createElement('div');
  bootContainer.className = 'entry-boot';
  container.appendChild(bootContainer);

  // Hide cursor during typing
  cursor.style.display = 'none';

  for (const msg of bootMessages) {
    if (isSequenceAborted) return;

    const line = document.createElement('div');
    line.className = `entry-boot-line entry-boot-line--${msg.style}`;
    bootContainer.appendChild(line);

    // Scale typing duration for mobile (slightly faster)
    const typeDuration = isMobileViewport() ? msg.duration * 0.8 : msg.duration;

    await new Promise<void>(resolve => {
      const tween = typeText(line, msg.text, typeDuration);
      tween.eventCallback('onComplete', resolve);
    });

    await delay(timing.bootLineDelay);
  }

  // Brief pause before fragments
  await delay(timing.postBootPause);
  if (isSequenceAborted) return;

  // Fade out boot messages with glitch
  glitchText(bootContainer, timing.glitchFadeDelay);
  await delay(timing.glitchFadeDelay);

  gsap.to(bootContainer, {
    opacity: 0,
    duration: 0.3,
    onComplete: () => bootContainer.remove()
  });

  await delay(timing.postGlitchDelay);
}

/**
 * Phase 2: Overlapping fragments with different positions
 */
async function runFragmentPhase(
  fragmentsContainer: HTMLElement,
  timing: ReturnType<typeof getTimingConfig>
): Promise<void> {
  const fragmentExcerpts = (excerpts as Excerpt[]).filter(e => e.phase === 2);

  // Shuffle fragments for variety
  const shuffled = [...fragmentExcerpts].sort(() => Math.random() - 0.5);

  // Show fewer fragments on mobile to reduce clutter
  const selectedFragments = shuffled.slice(0, timing.fragmentCount);

  // Calculate timing to distribute fragments across the phase
  const fragmentDelay = timing.fragmentPhaseDuration / (selectedFragments.length + 1);

  for (let i = 0; i < selectedFragments.length; i++) {
    if (isSequenceAborted) return;

    const excerpt = selectedFragments[i];
    await showFragment(fragmentsContainer, excerpt, i, timing);

    // Stagger the appearance
    await delay(fragmentDelay);
  }

  // Let last fragments linger briefly (shorter on mobile)
  await delay(timing.postFragmentDelay);
}

/**
 * Show a single fragment with animation
 */
async function showFragment(
  container: HTMLElement,
  excerpt: Excerpt,
  index: number,
  timing: ReturnType<typeof getTimingConfig>
): Promise<void> {
  const fragment = document.createElement('div');
  fragment.className = `entry-fragment entry-fragment--${excerpt.style}`;
  fragment.setAttribute('data-position', excerpt.position || 'center');

  // Apply position-based styling
  applyFragmentPosition(fragment, excerpt.position, index);

  container.appendChild(fragment);

  // Start invisible
  gsap.set(fragment, { opacity: 0 });

  // Fade in
  gsap.to(fragment, { opacity: 1, duration: 0.3 });

  // Scale animation duration for mobile
  const durationScale = isMobileViewport() ? 0.7 : 1;

  // Type or reveal based on style
  if (excerpt.style === 'bbs-glitch' || excerpt.style === 'system') {
    await corruptedReveal(fragment, excerpt.text, 0.4 * durationScale);
  } else if (excerpt.style === 'vector') {
    await textScramble(fragment, excerpt.text, excerpt.duration * durationScale);
  } else {
    await new Promise<void>(resolve => {
      const tween = typeText(fragment, excerpt.text, excerpt.duration * durationScale);
      tween.eventCallback('onComplete', resolve);
    });
  }

  // Linger then fade out (shorter on mobile)
  const lingerTime = timing.fragmentLingerBase + Math.random() * timing.fragmentLingerVariance;
  await delay(lingerTime);

  if (!isSequenceAborted) {
    const tl = gsap.timeline();
    activeTimelines.push(tl);

    tl.to(fragment, {
      opacity: 0,
      duration: 0.5,
      onComplete: () => fragment.remove()
    });
  }
}

/**
 * Apply position styling to a fragment
 */
function applyFragmentPosition(element: HTMLElement, position: string | undefined, index: number): void {
  const positions: Record<string, { top?: string; left?: string; right?: string; bottom?: string; transform?: string }> = {
    'center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    'offset-left': { top: '40%', left: '15%', transform: 'translateY(-50%)' },
    'offset-right': { top: '45%', right: '15%', transform: 'translateY(-50%)' },
    'top-left': { top: '15%', left: '10%' },
    'top-right': { top: '15%', right: '10%' },
    'bottom-left': { bottom: '20%', left: '10%' },
    'bottom-right': { bottom: '20%', right: '10%' },
    'random': {
      top: `${20 + Math.random() * 60}%`,
      left: `${10 + Math.random() * 80}%`,
      transform: `rotate(${(Math.random() - 0.5) * 6}deg)`
    }
  };

  const pos = positions[position || 'center'] || positions['center'];

  element.style.position = 'absolute';
  if (pos.top) element.style.top = pos.top;
  if (pos.left) element.style.left = pos.left;
  if (pos.right) element.style.right = pos.right;
  if (pos.bottom) element.style.bottom = pos.bottom;
  if (pos.transform) element.style.transform = pos.transform;

  // Apply index-based z-index
  element.style.zIndex = String(10 + index);

  // Slight random rotation for prose fragments
  if (!pos.transform && Math.random() > 0.7) {
    element.style.transform = `rotate(${(Math.random() - 0.5) * 3}deg)`;
  }
}

/**
 * Phase 3: Clear and show final question
 */
async function runFinalPhase(
  container: HTMLElement,
  fragmentsContainer: HTMLElement,
  timing: ReturnType<typeof getTimingConfig>
): Promise<void> {
  if (isSequenceAborted) return;

  // Fade out any remaining fragments
  const remainingFragments = fragmentsContainer.querySelectorAll('.entry-fragment');
  gsap.to(remainingFragments, {
    opacity: 0,
    duration: 0.5,
    stagger: 0.1
  });

  await delay(timing.fadeOutDelay);
  if (isSequenceAborted) return;

  // Clear fragments container
  fragmentsContainer.innerHTML = '';

  // Show transition message
  const transitionExcerpt = (excerpts as Excerpt[]).find(e => e.id === 'transition');
  if (transitionExcerpt) {
    const transitionEl = document.createElement('div');
    transitionEl.className = 'entry-text entry-text--center entry-text--transition';
    container.appendChild(transitionEl);

    // Scale duration for mobile
    const durationScale = isMobileViewport() ? 0.7 : 1;
    await new Promise<void>(resolve => {
      const tween = typeText(transitionEl, transitionExcerpt.text, transitionExcerpt.duration * durationScale);
      tween.eventCallback('onComplete', resolve);
    });

    await delay(timing.transitionPause);

    gsap.to(transitionEl, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => transitionEl.remove()
    });

    await delay(timing.transitionFadeDelay);
  }

  if (isSequenceAborted) return;

  // Show the final question with dramatic effect
  const questionExcerpt = (excerpts as Excerpt[]).find(e => e.id === 'question');
  if (questionExcerpt) {
    const questionEl = document.createElement('div');
    questionEl.className = 'entry-question';
    container.appendChild(questionEl);

    gsap.set(questionEl, { opacity: 0, scale: 0.95 });
    gsap.to(questionEl, { opacity: 1, scale: 1, duration: 0.5 });

    // Scale duration for mobile
    const durationScale = isMobileViewport() ? 0.7 : 1;
    await textScramble(questionEl, questionExcerpt.text, 2.5 * durationScale);
  }

  await delay(timing.questionRevealDelay);
  if (isSequenceAborted) return;

  // Show enter prompt
  const promptExcerpt = (excerpts as Excerpt[]).find(e => e.id === 'enter-prompt');
  if (promptExcerpt) {
    const promptEl = document.createElement('div');
    promptEl.className = 'entry-prompt';
    promptEl.textContent = promptExcerpt.text;
    promptEl.style.opacity = '0';
    container.appendChild(promptEl);

    gsap.to(promptEl, {
      opacity: 1,
      duration: 0.8,
      delay: 0.5
    });
  }
}

/**
 * Clean up event listeners and animations
 */
export function cleanupEntrySequence(): void {
  isSequenceAborted = true;

  // Kill all active GSAP timelines
  activeTimelines.forEach(tl => tl.kill());
  activeTimelines = [];

  // Clear all active timeouts
  activeTimeouts.forEach(id => window.clearTimeout(id));
  activeTimeouts = [];

  // Remove event listeners
  const containers = document.querySelectorAll('.entry-container');
  containers.forEach(container => {
    const handler = (container as any).__proceedHandler;
    if (handler) {
      container.removeEventListener('click', handler);
      document.removeEventListener('keydown', handler);
    }
  });

  // Kill any lingering GSAP animations on entry elements
  gsap.killTweensOf('.entry-fragment');
  gsap.killTweensOf('.entry-text');
  gsap.killTweensOf('.entry-question');
  gsap.killTweensOf('.entry-prompt');
}
